/**
 * /guides/when-should-wedding-guests-rsvp
 *
 * "When Should Wedding Guests RSVP? A Practical Wedding Timeline" — the fourth
 * production guide, and the first one that does not belong to the seating
 * cluster. It answers the question a couple asks before any of the seating
 * guides become relevant: what date do we put on the invitation?
 *
 * Its argument is that the deadline is derived, not chosen. Three to four weeks
 * is offered as a starting point and never as a rule; the article's actual
 * method is to work backward from the vendor final-count deadline, leaving room
 * for follow-up. Every number in it is hedged for that reason — "a practical
 * starting point", "a useful buffer", "these are planning examples, not
 * universal deadlines". Keep the hedges. A reader whose caterer needs numbers
 * five weeks out is not wrong, and an absolute rule here would tell them they
 * are.
 *
 * The five milestones the guide deliberately keeps apart — RSVP deadline,
 * vendor final-count deadline, invitation send date, follow-up period, and the
 * final confirmed guest count — are the whole point of the piece. Do not
 * collapse any two of them into a single date in a later edit.
 *
 * ── The claims this guide is allowed to make ──────────────────────────────
 * Four, all in the closing section, and nothing beyond them:
 *
 *   - "Agapaé's RSVP tools are available on the Heirloom plan."  (the note)
 *     True at `api/constants/planFeatures.mjs`: RSVP is part of
 *     `invitationProduct`, which is `pro` (Heirloom) only.
 *   - couples manage invited guests
 *   - couples review the responses
 *   - that information is used by the planning that comes after it
 *
 * And the things this guide must never say, because none of them exist:
 * automatic reminders, automatic RSVP follow-ups, automatic guest-count
 * finalization, automatic seating generation, personalized RSVP emails,
 * automatic vendor notifications, unlimited RSVPs, or real-time synchronization
 * with a vendor. The article gives reminder and follow-up advice at length —
 * that advice is addressed to the reader, never described as something the
 * product does on their behalf. It must stay that way.
 *
 * Nothing here may imply that RSVP records and photo-gallery records are
 * identity-linked; they are not, and the guide never mentions the gallery.
 *
 * ── Three structural notes ────────────────────────────────────────────────
 * The source article marks several lines bold. `BLOCK_TYPES` has no inline
 * emphasis, and teaching both renderers about one would be infrastructure work
 * this content change has no reason to do. Each bold line is therefore rendered
 * as what it actually is, following the call the third guide wrote down: a
 * standalone bold sentence or question becomes an ordinary paragraph, and a
 * bold label introducing the content beneath it becomes a `subheading` (an H3).
 * That is why the six milestones of "A Simple Wedding RSVP Timeline" are H3s —
 * they are also the most scannable thing on the page.
 *
 * The work-backward chain in "Work Backward From Your Venue and Caterer" is a
 * `steps` list. The source drew it with arrows between the lines; the <ol>
 * draws the same sequence, so the glyphs are gone for the same reason the third
 * guide dropped its ordinal words. Reading 1 to 6 downward IS the backward walk
 * the surrounding prose describes.
 *
 * No copy in this file may contain "&", "<" or ">". The generator escapes them
 * correctly, but `guides.seo.test.mjs` compares against tag-stripped HTML
 * without decoding entities, so a string containing one is reported missing
 * from a document that in fact carries it.
 */

const whenShouldWeddingGuestsRsvpEn = {
  slug: "when-should-wedding-guests-rsvp",

  title: "When Should Wedding Guests RSVP? A Practical Wedding Timeline",
  metaTitle: "When Should Wedding Guests RSVP? Wedding RSVP Timeline | Agapaé",
  metaDescription:
    "Find the right RSVP deadline for your wedding, when to send invitations, and how much time to leave for late responses and final guest counts.",

  eyebrow: "Wedding RSVP",

  hero: {
    src: "/assets/images/moments/moment_3.jpg",
    // Describes the photograph, not the keyword: a backlit black and white
    // frame of a couple walking away from the camera into low sun. There is no
    // invitation and no deadline in it, and saying otherwise would be a lie
    // told to exactly the readers who cannot see it.
    alt: "A black and white photograph of a bride and groom seen from behind, walking hand in hand into low evening sun, her veil and gown catching the light",
    width: 6558,
    height: 3659,
  },

  // Absent until the guide is genuinely published. Adding one means adding the
  // other, and `dateModified` may not precede `datePublished`.
  // datePublished: "YYYY-MM-DD",
  // dateModified: "YYYY-MM-DD",

  intro: [
    `Setting an RSVP deadline sounds simple until you realize that the date affects almost everything that comes next.`,
    `You need to know how many guests are coming before you can finalize your guest list, confirm numbers with your venue and caterer, review dietary requirements, and finish your seating plan.`,
    `So when should wedding guests RSVP?`,
    `For most weddings, a practical starting point is around three to four weeks before the wedding. But the best deadline is not simply a number on a calendar. It is the date that gives you enough time to follow up with guests who have not responded and still meet the final guest-count deadline set by your venue and caterer.`,
    `That means the most useful way to choose your RSVP date is to work backward from the deadlines that matter to your wedding.`,
  ],

  sections: [
    {
      heading: "When Should Wedding Guests RSVP?",
      content: [
        `For a typical wedding, set the RSVP deadline around three to four weeks before the wedding.`,
        `This gives you a useful buffer between the date guests are expected to reply and the point when you need to finalize the numbers with your venue or caterer.`,
        `For example, if your caterer needs a final guest count two weeks before the wedding, setting your RSVP deadline three to four weeks before the wedding gives you some time to:`,
        {
          type: "list",
          items: [
            `identify guests who have not responded`,
            `follow up with them`,
            `resolve unclear responses`,
            `confirm changes`,
            `finalize your guest list`,
            `prepare the seating plan`,
          ],
        },
        `The exact timeline can be shorter or longer depending on your vendors and the needs of your guests.`,
        `The important point is not to choose an RSVP date in isolation.`,
      ],
    },
    {
      heading: "Why Three to Four Weeks Is a Useful Starting Point",
      content: [
        `The three-to-four-week range works well because it creates a practical gap between your guests' response deadline and your own final planning deadlines.`,
        `If the RSVP deadline is too close to the wedding, a few late responses can create unnecessary pressure.`,
        `You may still be trying to determine:`,
        {
          type: "list",
          items: [
            `how many meals to order`,
            `how many tables you need`,
            `where guests should sit`,
            `which guests are actually attending`,
            `whether any guest has changed their plans`,
          ],
        },
        `On the other hand, setting the RSVP deadline much earlier than necessary can create a different problem.`,
        `Guests may not yet know their availability, travel arrangements or plans. An unnecessarily early deadline can lead to incomplete answers and later changes.`,
        `A useful RSVP deadline therefore balances two needs:`,
        // Two bold lines standing on their own in the source, and each a
        // complete sentence. There is no inline emphasis in the vocabulary.
        `Guests need enough time to make a decision.`,
        `You need enough time to act on their answers.`,
      ],
    },
    {
      heading: "Work Backward From Your Venue and Caterer",
      content: [
        `Before choosing your RSVP deadline, ask your venue and caterer:`,
        `When do you need the final guest count?`,
        `This is one of the most important questions in the entire RSVP process.`,
        `Your vendors may have a specific deadline in your contract. That date should take priority over a generic wedding-planning rule.`,
        `Once you know the final-count deadline, work backward.`,
        `For example:`,
        {
          // The source drew this chain with arrows between the lines. The <ol>
          // draws the same sequence, so the glyphs are gone and the copy is
          // untouched.
          type: "steps",
          items: [
            `Wedding day`,
            `Venue/caterer final count`,
            `Leave time for follow-up and corrections`,
            `RSVP deadline`,
            `Allow enough time for guests to receive the invitation and respond`,
            `Invitation send date`,
          ],
        },
        `The exact number of days between these milestones will vary.`,
        `The principle stays the same: start with the deadline you cannot miss, then work backward.`,
      ],
    },
    {
      heading: "When Should You Send Wedding Invitations?",
      content: [
        `For many weddings, formal invitations are sent around six to eight weeks before the wedding.`,
        `However, this is not a universal rule.`,
        `You may want to send them earlier when:`,
        {
          type: "list",
          items: [
            `many guests need to travel`,
            `guests are coming from different countries`,
            `the wedding falls around a major holiday`,
            `accommodation needs to be arranged`,
            `your guests need to request time off work`,
            `there is no save-the-date`,
            `the wedding is effectively a destination event for much of the guest list`,
          ],
        },
        `The invitation send date and RSVP deadline should therefore be planned together.`,
        `There is little benefit in choosing an RSVP deadline first and then discovering that guests only have a very short window to receive and answer the invitation.`,
      ],
    },
    {
      heading: "How Much Time Should Guests Have to Respond?",
      content: [
        `Guests need enough time to determine whether they can attend and, when necessary, make travel or other arrangements.`,
        `For a local wedding, the invitation and RSVP period can be relatively compact.`,
        `For guests who need to travel, the decision may involve:`,
        {
          type: "list",
          items: [
            `flights or long-distance transport`,
            `accommodation`,
            `time off work`,
            `childcare`,
            `coordinating with other members of their household`,
          ],
        },
        `This is why there is no single RSVP timeline that works equally well for every wedding.`,
        `Think about what your guests actually need to make a reliable commitment.`,
        `The more complicated attendance is, the more useful it is to give guests additional lead time.`,
      ],
    },
    {
      heading: "What About Destination Weddings?",
      content: [
        `Destination weddings generally need an earlier timeline because attending involves more planning.`,
        `Guests may need to arrange flights, accommodation, passports, time off work and other travel details.`,
        `As a result, couples often set RSVP deadlines earlier than they would for a local wedding.`,
        `A practical starting point is around four to six weeks before the wedding, with invitations sent earlier as well.`,
        `But even here, work backward from your actual vendor deadlines.`,
        `If your venue requires the final count earlier, your RSVP deadline needs to accommodate that.`,
        `If most of your guests are travelling internationally, giving them more time can also make the overall experience less stressful.`,
        `There is no need to force a destination wedding into the same timeline as a local wedding simply because a generic checklist says so.`,
      ],
    },
    {
      heading: "What If Many Guests Need to Travel?",
      content: [
        `You do not need to classify your wedding as a formal "destination wedding" for travel to affect your RSVP timeline.`,
        `A wedding can be in your hometown while half the guest list lives elsewhere.`,
        `If many guests need to book flights, accommodation or time away from work, consider giving them more time to respond.`,
        `The key question is:`,
        `How much time does a typical guest need to make a realistic decision?`,
        `If the answer is longer than the standard local-wedding window, adjust your timeline.`,
        `Your RSVP deadline should serve your actual guest list, not an abstract rule.`,
      ],
    },
    {
      heading: "When Should You Remind Guests to RSVP?",
      content: [
        `Do not wait until the wedding is approaching to discover that several guests have not replied.`,
        `A simple reminder before the deadline can help guests who:`,
        {
          type: "list",
          items: [
            `missed the original invitation`,
            `intended to respond but forgot`,
            `are waiting to confirm travel`,
            `assumed someone else in their household had replied`,
          ],
        },
        `A practical approach is to send a friendly reminder about a week before the RSVP deadline.`,
        `After the deadline passes, review the remaining non-responses and follow up directly where appropriate.`,
        `The tone matters.`,
        `A reminder does not need to sound like a demand. The goal is simply to make it easy for guests to give you a clear answer.`,
      ],
    },
    {
      heading: "What Should You Do When Someone Misses the Deadline?",
      content: [
        `A missed RSVP deadline does not automatically mean the guest is attending or declining.`,
        `You need a clear answer.`,
        `Start by checking whether the guest may have responded through another channel or whether another member of their household submitted the response.`,
        `If there is still no answer, contact them directly.`,
        `Keep the message simple:`,
        `You are finalizing the guest count and would appreciate confirmation of whether they will be attending.`,
        `Once you have a response, update your guest information rather than relying on memory or an old message thread.`,
        `This is particularly important once you begin creating your seating plan.`,
      ],
    },
    {
      heading: "Should You Set the Same Deadline for Everyone?",
      content: [
        `Usually, yes.`,
        `A single RSVP deadline keeps the process easier to understand and makes it simpler to track who has responded.`,
        `However, your overall timeline can still account for different guest needs.`,
        `For example, you can send invitations earlier to everyone if many guests are travelling.`,
        `You generally do not need one RSVP date for local guests and another for international guests unless there is a specific logistical reason.`,
        `The simpler the system, the easier it is to manage.`,
      ],
    },
    {
      heading: "A Simple Wedding RSVP Timeline",
      content: [
        `Here is a practical example for a local wedding, which allows a little more time than the six-to-eight-week starting point above.`,
        // Each bold label in the source introduces the line beneath it, so each
        // becomes an H3 — which is also what makes this section scannable.
        { type: "subheading", text: "8–10 weeks before the wedding" },
        `Send the formal invitations, giving guests plenty of time to receive them and respond.`,
        { type: "subheading", text: "6–8 weeks before" },
        `Guests receive the invitation and begin making plans.`,
        { type: "subheading", text: "About 4 weeks before" },
        `RSVP deadline.`,
        { type: "subheading", text: "About 3 weeks before" },
        `Follow up with guests who have not responded.`,
        { type: "subheading", text: "About 2 weeks before" },
        `Finalize the guest count with the venue and caterer, according to their required deadline.`,
        { type: "subheading", text: "After the final count" },
        `Complete the remaining planning based on the confirmed attendees, including the seating plan.`,
        `These are planning examples, not universal deadlines.`,
        `If your venue or caterer requires the final number earlier, move the RSVP deadline earlier as well.`,
      ],
    },
    {
      heading: "The RSVP Deadline Is Not Your Final Guest Count",
      content: [
        `This distinction is easy to miss.`,
        `The RSVP deadline is the date by which you ask guests to respond.`,
        `The final guest count is the number you actually provide to your vendors after you have reviewed the responses and resolved outstanding cases.`,
        `Those two dates do not have to be the same.`,
        `That gap is useful.`,
        `It gives you time to deal with:`,
        {
          type: "list",
          items: [
            `missing responses`,
            `unclear responses`,
            `changes in attendance`,
            `household or party-size corrections`,
            `final guest-list checks`,
          ],
        },
        `Think of the RSVP deadline as the start of your final guest-count process, not the end of it.`,
      ],
    },
    {
      heading: "From RSVP to the Rest of Your Wedding Planning",
      content: [
        `Your RSVP responses become useful information for several other parts of the wedding.`,
        `Once you know who is attending, you can move on to tasks such as:`,
        {
          type: "list",
          items: [
            `confirming the final guest count`,
            `reviewing attendance by event or moment`,
            `organizing dietary information`,
            `preparing your seating plan`,
            `confirming table capacity`,
            `communicating final numbers to vendors`,
          ],
        },
        `This is why it is worth keeping RSVP information organized rather than treating each response as a separate message.`,
        `Your seating plan, in particular, should be based on the guests you actually expect to attend rather than an early version of the invitation list.`,
        {
          type: "p",
          text: [
            `If you want to see the next step in that process, read `,
            {
              text: "How to Create a Wedding Seating Plan: From RSVP to Every Table",
              href: "/guides/wedding-seating-plan",
            },
            `.`,
          ],
        },
      ],
    },
    {
      // The source article titles this section "Where This Fits in Agapaé",
      // which is the heading the `related` block below already renders as an
      // H2. Two H2s reading the same words is a defect, and all three earlier
      // guides resolved it the same way: the closing prose section is named for
      // what it does, and "Where this fits in Agapaé" stays with the related
      // block.
      heading: "From Your RSVP Deadline to a Confirmed Guest List",
      content: [
        {
          // The only plan-availability claim in the guide, in the same place
          // and the same block type the three earlier guides put theirs.
          type: "note",
          text: `Agapaé's RSVP tools are available on the Heirloom plan.`,
        },
        `The RSVP experience allows couples to manage invited guests and review responses before moving on to the next stages of wedding planning.`,
        {
          type: "p",
          text: [
            `For the practical process of turning confirmed attendees into a seating plan, see `,
            {
              text: "How to Create a Wedding Seating Plan: From RSVP to Every Table",
              href: "/guides/wedding-seating-plan",
            },
            `.`,
          ],
        },
        {
          type: "p",
          text: [
            `For the product-specific RSVP experience, see `,
            { text: "Wedding RSVP", href: "/wedding-rsvp" },
            `.`,
          ],
        },
      ],
    },
  ],

  faqHeading: "Frequently Asked Questions",

  // Visible copy, always expanded. `faqSchema` is deliberately not set, exactly
  // as on the three earlier guides: Google restricts FAQ rich results to
  // government and health sites, so the markup would buy nothing while
  // committing us to a second copy of every answer.
  faq: [
    {
      q: `When should wedding guests RSVP?`,
      a: `For most weddings, a practical RSVP deadline is around three to four weeks before the wedding. The final date should be based on when your venue and caterer need the final guest count, with enough time left to follow up with guests who have not responded.`,
    },
    {
      q: `Is 4 weeks enough time to RSVP to a wedding?`,
      a: `Four weeks is a common and practical starting point for many weddings. However, guests who need to travel may benefit from more time, and your deadline should always leave enough room to meet your venue and caterer's final-count requirements.`,
    },
    {
      q: `How early should wedding invitations be sent?`,
      a: `Many couples send formal wedding invitations around six to eight weeks before the wedding. Send them earlier when guests need more time to arrange travel, accommodation, time off work or other logistics.`,
    },
    {
      q: `Should destination wedding RSVPs be due earlier?`,
      a: `Often, yes. Destination weddings and weddings with many travelling guests generally benefit from a longer RSVP timeline because guests need to make additional travel arrangements. A deadline around four to six weeks before the wedding can be a useful starting point, but the actual date should be determined by your vendor deadlines and guest needs.`,
    },
    {
      q: `What should I do if a guest does not RSVP by the deadline?`,
      a: `Follow up directly and ask for a clear answer. Before doing so, check whether another member of the household has already responded. Once you receive the answer, update your guest information so your final count and seating plan are based on confirmed attendees.`,
    },
  ],

  related: {
    heading: "Where this fits in Agapaé",

    // Deliberately empty, matching all three earlier guides. The seating guide
    // is already linked from the prose at the two points where a reader
    // actually needs it; a third link under this heading would be a link built
    // for a crawler rather than for the person reading.
    guides: [],

    product: [
      {
        href: "/wedding-rsvp",
        label: "Wedding RSVP",
        blurb:
          "See how RSVPs work in Agapaé, from the guests you invite to the responses you review before the rest of your planning. RSVP tools are available on the Heirloom plan.",
      },
    ],
  },
};

export default whenShouldWeddingGuestsRsvpEn;
